import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { useVisitorState } from "@/hooks/useVisitorState";

const CITY_CARDS = [
  {
    key: "miami",
    city: "Brickell, Miami, FL",
    label: "Florida Residents",
    headline: "Ketamine Therapy for Hyper-Independent Women in Brickell, Miami",
    path: "/specialties/online-ketamine-therapy-women-miami",
    testId: "city-card-miami",
  },
  {
    key: "cumberland",
    city: "Cumberland, ME",
    label: "Maine Residents",
    headline: "Ketamine Therapy for Hyper-Independent Women in Cumberland",
    path: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine",
    testId: "city-card-cumberland",
  },
  {
    key: "portland",
    city: "Portland, ME",
    label: "Maine Residents",
    headline: "Ketamine Therapy for Hyper-Independent Women in Portland",
    path: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine",
    testId: "city-card-portland",
  },
  {
    key: "dc",
    city: "Washington, DC",
    label: "DC Residents",
    headline: "Ketamine Therapy for Hyper-Independent Women in Washington, DC",
    path: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc",
    testId: "city-card-dc",
  },
];

export const CityPackagesGrid = ({
  eyebrow = "Where do you live?",
  heading = "See the offering built for your state",
  subheading = "I'm licensed in Florida, Maine, & DC — pick your state to see the package designed for you.",
  testIdPrefix = "city-packages",
  dcTarget = "package",
  showGroupImmersion = false,
}) => {
  const visitorCity = useVisitorState();

  const cards = CITY_CARDS.map((c) => c);

  if (showGroupImmersion) {
    cards.push({
      key: "group",
      city: "FL · ME",
      label: "Group Immersion",
      headline:
        "9 Weeks Immersive Group Intensive For Women in Florida and Maine",
      path: "/specialties/ketamine-therapy-group-immersion-fl-me",
      testId: "city-card-group",
    });
  }

  return (
    <div data-testid={testIdPrefix}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <span className="inline-flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-[0.18em]">
          <MapPin className="h-4 w-4" />
          {eyebrow}
        </span>
        <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mt-3 leading-tight">
          {heading}
        </h3>
        <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">{subheading}</p>
      </motion.div>

      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-5 ${
          cards.length > 4 ? "lg:grid-cols-3 xl:grid-cols-5" : "lg:grid-cols-4"
        }`}
      >
        {cards.map((c) => {
          const isMatch = visitorCity === c.key;
          return (
            <Link
              to={c.path}
              key={c.path}
              data-testid={`${testIdPrefix}-${c.testId}`}
              className="group block h-full"
            >
              <motion.div
                initial={false}
                animate={
                  isMatch
                    ? { scale: [1, 1.015, 1], transition: { duration: 1.2 } }
                    : {}
                }
                className="h-full"
              >
                <Card
                  className={`p-6 h-full bg-white rounded-2xl transition-all flex flex-col ${
                    isMatch
                      ? "border-2 border-accent/70 ring-4 ring-accent/20 shadow-hover bg-gradient-to-br from-accent/5 to-white"
                      : "border border-border/40 hover:border-primary/40 hover:shadow-hover"
                  }`}
                  data-visitor-match={isMatch ? "true" : "false"}
                >
                  {isMatch && (
                    <div
                      className="mb-3 inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-accent/20 text-accent text-[10px] font-semibold uppercase tracking-[0.14em]"
                      data-testid={`${testIdPrefix}-match-badge`}
                    >
                      <Sparkles className="h-3 w-3" />
                      Best match for you
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-[0.14em]">
                    <MapPin className="h-3.5 w-3.5" />
                    {c.label}
                  </div>
                  <p className="font-serif text-lg font-semibold text-foreground mt-2 leading-snug">
                    {c.city}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-grow">
                    {c.headline}
                  </p>
                  <span
                    className={`mt-4 inline-flex items-center gap-1.5 font-medium text-sm transition-all ${
                      isMatch
                        ? "text-accent gap-2"
                        : "text-primary group-hover:gap-2.5"
                    }`}
                  >
                    {isMatch ? "Start here" : "See this offering"}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Card>
              </motion.div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default CityPackagesGrid;
